import axios, { AxiosResponse } from 'axios';

import { useAppDispatch, useAppSelector } from '@/ui/store/helperRedux';
import { RootState } from '@/ui/store/store';
import { login, logout, updClient } from '@/ui/store/slices/userSlice';

import ClientRepository from '@/data/repositories/ClientRepository';

import CartController from '@/controllers/CartController';

import { loginUser } from '@/domain/useCases/loginUseCase';
import { registerUser } from '@/domain/useCases/registerUserCase';
import { getInfoUserCase } from '@/domain/useCases/getInfoUserCase';
import updateUser from '@/domain/useCases/updateUserCase';
import changePasswd from '@/domain/useCases/changePasswdUseCase';

import { translate } from '@/domain/utils/translate';
import { IClient, IClientDetailsValidation, ILoginData, IRegistrationData } from '@/domain/interfaces/IUser';
import { ILoginApi, IUserResponse } from '@/domain/interfaces/ILoginResponse';
import { IChangePaswd } from '@/domain/interfaces/IChangePaswd';
import { IRegisterResponse } from '@/domain/interfaces/IRegisterResponse';
import { ICart } from '@/domain/interfaces/ICart';


/** Representa el controlador que maneja los eventos de los casos de uso del usuario
 * @returns {object} Funciones del controlador
 */
const UserController = () => {
  
  const EXIST_ERROR = 'CLI009';
  const EXIST_MESSAGE = translate('session.exist_account');
  const BACK_PHONE_ERROR = 'CLI008';
  const BACK_PHONE_MESSAGE = translate('user.invalid_phone');

  /** Actualización estados Redux */
  const dispatch = useAppDispatch();
  
  /** Controlador de carrito */
  const { updateCartMainInfo } = CartController();
  
  /** Estado redux de carrito */
  const cart = useAppSelector((state: RootState) => state.cartState) as ICart;

  /** Instancia del Repository
   * @const {ClientRepository}
   */
  const clientRepository = ClientRepository.instance;

  /** Realiza el proceso de login
   * @param {ILoginData} user - data del usuario a loguear
   * @param {boolean} isALogin - el método sirve para solo validar o validar y hacer login
   * @param {boolean} keep - mantener la sesión
   * @returns {IUserResponse} IUserResponse - Mensajes, estado y data
   */
  const doLogin = async (user: ILoginData, isALogin: boolean = true, keep: boolean = true): Promise<IUserResponse> => {
    /** Realiza las validaciones y llamados al API     */
    const userLogued: IUserResponse = await loginUser(user, isALogin);

    if (userLogued.isError) {
      return userLogued;
    }

    /** Si se solicitó el login se actualiza redux y localeStorage     */
    if (isALogin) {
      /** Actualiza el estado Redux del usuario si logro loguear       */
      dispatch(login(userLogued.data));

      /** Actualiza el id del usuario en el carrito */
      updateUserIdInCart(userLogued.data?.id ?? null);

      /** Mantiene la sesión si el usuario lo marcó */
      if (keep) {
        keepCredentials(userLogued);
      } else {
        cleanCredentialsLocalStorage();
      }
    }
    return userLogued;
  };

  /**
   * Realiza el proceso de registro
   * @param {IRegistrationData} user - data del usuario a loguear
   * @param {boolean} keep - Si desea mantener la sesión
   * @returns {IClientDetailsValidation[]} IClientDetailsValidation - Mensajes, estado y data
   */
  const clientRegistration = async (user: IRegistrationData, validationOnly: boolean = true, keep: boolean = true): Promise<IRegisterResponse> => {
    const registerResponse: IRegisterResponse = {
      data: null,
      user: null,
      errors: [],
    };

    /**
     * Realiza las validaciones y realiza el registro
     */
    const userValidation: IClientDetailsValidation[] = registerUser(user);
    if (userValidation.length < 1) {
      if (!validationOnly) {
        try {
          /** Registra el cliente */
          const dataRegister: AxiosResponse<ILoginApi> = await clientRepository.registerUser(user);
          if (dataRegister) {
            /** Mantiene la sesión si el usuario lo marcó */
            if (keep) {
              keepCredentials({
                data: dataRegister.data,
                isError: false,
                message: [],
                inputError: null,
              });
            } else {
              cleanCredentialsLocalStorage();
            }

            /** Actualiza el estado Redux del usuario si logro loguearse */
            dispatch(login(dataRegister.data));
            /** Actualiza el id del usuario en el carrito */
            updateUserIdInCart(dataRegister.data?.id ?? null);
            
            registerResponse.data = { ...dataRegister.data };
          }
        } catch (repositoryError) {
          if (axios.isAxiosError(repositoryError)) {
            if (repositoryError.response?.status && repositoryError.response.status === 422) {
              if (repositoryError.response.data[0].code === BACK_PHONE_ERROR) {
                registerResponse.errors.push({
                  error: true,
                  field: 'PHONE',
                  message: BACK_PHONE_MESSAGE,
                  showAlert: true,
                });
              } else {
                registerResponse.errors.push({
                  error: true,
                  field: 'EMAIL',
                  message: EXIST_MESSAGE,
                  showAlert: true,
                });
              }
            }
          } else {
            console.error('Unknow error: ', repositoryError);
          }
        }
      }
    } else {
      registerResponse.errors.splice(0, registerResponse.errors.length, ...userValidation);
    }

    return registerResponse;
  };

   /**
   * Realiza validación de proceso de registro
   * @param {IRegistrationData} user - data del usuario a loguear
   * @returns {IClientDetailsValidation[]} IClientDetailsValidation - Mensajes, estado y data
   */
  const validateUserRegister = async (user: IRegistrationData): Promise<IRegisterResponse> => {

    const registerResponse: IRegisterResponse = {
      data: null,
      user: null,
      errors: [],
    };

    /**
     * Realiza las validaciones y realiza el registro
     */
    try {
      await clientRepository.validateUserRegister(user);
    } catch (repositoryError) {
      if (axios.isAxiosError(repositoryError)) {
        if (repositoryError.response?.status && repositoryError.response.status === 422) {
          
          const phoneError = repositoryError.response.data.find(error=>error.code === BACK_PHONE_ERROR);
          if (phoneError) {
            registerResponse.errors.push({
              error: true,
              field: 'PHONE',
              message: BACK_PHONE_MESSAGE,
              showAlert: true,
            });
          }
          const existError = repositoryError.response.data.find(error=>error.code === EXIST_ERROR);
          if (existError) {
            registerResponse.errors.push({
              error: true,
              field: 'EMAIL',
              message: EXIST_MESSAGE,
              showAlert: true,
            });
          }
        }
      } else {
        console.error('Unknow error: ', repositoryError);
      }
    }

    return registerResponse;
  }

  /**
   * Realiza el proceso de logout
   */
  const doLogout = (): void => {
    /** Limpia localStorage */
    cleanCredentialsLocalStorage();

    /** Limpia el id del usuario del carrito */
    updateUserIdInCart(null);

    /** Limpia el estado Redux del usuario */
    dispatch(logout());
  };

  /**
   * Realiza actualización de información de cliente/usuario
   * @param {IClient} user - data del usuario a loguear
   * @param {number} id - ID del usuario a actualizar
   * @param {string} token - token valido
   * @param {boolean} toSend - Solo validación o validación y submit
   * @returns {IUserResponse} IUserResponse - Mensajes, estado y data
   */
  const updateClient = async (
        user: IClient,
        id: number = 0,
        token: string = '',
        toSend: boolean,
      ): Promise<IUserResponse> => {
        
    /**
     * Realiza las validaciones y realiza el registro
     */
    const userValidation: IUserResponse = await updateUser(user, id, token, toSend);

    /**
     * si existen errores almacena true
     */
    let userRegistered: boolean = userValidation.isError;

    /** luego de actualización efectiva */
    if (!userRegistered && toSend) {
      /** Si se modificó el email */
      if (user.email && user.email !== null) {
        localStorage.setItem('ilisPlacesEmail', user.email!);
        dispatch(updClient({ email: user.email }));
      }
      /** Si se modificó el nombre */
      if (user.firstName && user.firstName !== null) {
        localStorage.setItem('ilisPlacesName', user.firstName);
        dispatch(updClient({ name: user.firstName }));
      }
      /** Si se modificó el apellido */
      if (user.lastName && user.lastName !== null) {
        localStorage.setItem('ilisPlacesLastName', user.lastName!);
        dispatch(updClient({ lastName: user.lastName! }));
      }
      /** Si se modificó la identificación */
      if (user.identification || user.idType) {
        localStorage.setItem('ilisPlacesOfficialIdUser', String(user.identification));
        localStorage.setItem('ilisPlacesTypeIdUser', String(user.idType));
        dispatch(updClient({
          officialIdUser: user.identification,
          idTypeIdUser: user.idType,
        }));
      }
      /** Si se modificó el apellido */
      if (user.phone && user.phone !== null) {
        localStorage.setItem('ilisPlacesUserPhone', JSON.stringify(user.phone) ?? '');
        dispatch(updClient({ userPhone: user.phone }));
      }
    }

    return userValidation;
  };

  /** Devuelve información del usuario
   * @param {string} email - email a consultar
   * @param {string} token - token valido
   * @returns {IUserResponse} IUserResponse - Mensajes, estado y data
   */
  const getUser = async (email: string = '', token: string = ''): Promise<IUserResponse> => {
    /** Realiza llamados al API y genera mensajes de error */
    const user: IUserResponse = await getInfoUserCase(email, token);

    return user;
  };

  /**
   *  Actualiza localStorage luego de login efectivo
   * @param {IUserResponse} user - Mensajes, estado y data del usuario encontrado previamente
   */
  const keepCredentials = (user: IUserResponse): void => {
    const userClient = 'CLIENT';
    const userAdmin = 'ADMIN';
    const loginTime: number = new Date().getTime();

    localStorage.setItem('ilisPlacesEmail', user.data!.email);
    localStorage.setItem('ilisPlacesActiveUser', user.data!.status);
    localStorage.setItem('ilisPlacesToken', user.data!.token);
    localStorage.setItem('ilisPlacesName', user.data!.firstName);
    localStorage.setItem('ilisPlacesLastName', user.data!.lastName);
    if (user.data!.officialId !== null) {
      localStorage.setItem('ilisPlacesOfficialIdUser', user.data!.officialId);
    }
    if (user.data!.officialIdType !== null) {
      localStorage.setItem('ilisPlacesTypeIdUser', user.data!.officialIdType);
    }
    localStorage.setItem('ilisPlacesUserPhone', user.data!.phone ? JSON.stringify(user.data!.phone) : "");
    localStorage.setItem('ilisPlacesPosition', user.data!.position);
    localStorage.setItem('ilisPlacesIdUser', String(user.data!.id));
    localStorage.setItem('ilisPlacesUserStore', String(user.data!.storeId));
    localStorage.setItem('ilisPlacesUserOfficeID', String(user.data!.branchOffice?.id));
    localStorage.setItem('ilisPlacesUserOfficeName', String(user.data!.branchOffice?.name));
    localStorage.setItem('ilisLoginDate', String(loginTime));

    if (user.data!.roles.includes(userClient) && user.data!.roles.length > 1) {
      localStorage.setItem('ilisPlacesKindUser', userAdmin);
    } else {
      localStorage.setItem('ilisPlacesKindUser', userClient);
    }

    localStorage.setItem('ipUser', '1.5.5.5');
  };

  /**
   * Realiza actualización de contraseña de cliente/usuario
   * @param {IChangePaswd} passwords - data del usuario a loguear
   * @param {boolean} onlyPassword - flag para saber si debe validar unicamente el password actual
   * @param {number} id - ID del cliente
   * @param {string} token - token valido
   * @param {boolean} toSend - flag para saber si debe enviar al back
   * @param {string} email - email del usuario activo
   * @returns {IUserResponse} IUserResponse - Mensajes, estado y data
   */
  const updatePassword = async (
    passwords: IChangePaswd,
    onlyPassword: boolean = true,
    id: number = 0,
    token: string = '',
    toSend: boolean,
    email: string = '',
  ): Promise<IUserResponse> => {
    /**
     * Realiza las validaciones y realiza el registro
     */
    const userValidation: IUserResponse = await changePasswd(passwords, onlyPassword, id, token, toSend, email);

    return userValidation;
  };

  /**
   *  Clean localStorage
   */
  const cleanCredentialsLocalStorage = (): void => {
    // Obtiene todas las claves almacenadas en localStorage
    const keys = Object.keys(localStorage);

    // Itera sobre las claves y elimina aquellas que no sean 'cart'
    keys.forEach(function (key) {
      if (key !== 'IlisPlacesCart') {
        localStorage.removeItem(key);
      }
    });
  };

  /**
   * Actualiza el id del usuario en el carrito
   * 
   * @param {number | null} id - cliente
   */
  const updateUserIdInCart = (id:number | null):void => {
    const cartToUpdate:ICart = JSON.parse(JSON.stringify(cart));
    if (cartToUpdate) {
      cartToUpdate.clientId = id == null ? undefined : id;
      updateCartMainInfo(cartToUpdate);
    }
  }
  

  /** Renueva el token de usuario logueado
   * @param {string} token
   * @returns {IUserResponse} IUserResponse - Mensajes, estado y data
   */
  const tokenRenovation = async (token: string = ''): Promise<boolean> => {
    try {
      /** Solicita renovación del token al back   */
      const renovation = await clientRepository.renovateToken(token);

      if (renovation.status !== 200) {
        return false;
      }

      /** Objeto response */
      const response: IUserResponse = {
        data: renovation.data,
        isError: false,
        inputError: null,
        message: [],
      };

      /** Actualiza el estado Redux del usuario si logro renovar */
      dispatch(login(renovation.data));
      keepCredentials(response);

      return true;
    } catch (error) {
      console.error('Token', error);
      return false;
    }
  };

  /**
   * Solicita código de autoriación cambio contraseña y notifica
   *
   * @param {string} email - contraseña actual y nueva
   * @returns {boolean}
   */
  const getAuthCode = async (email: string = '', newEmail: string = ''): Promise<boolean> => {
    try {
      const authCode = await clientRepository.getAuthCode(email, newEmail);
      return authCode.status === 200;
    } catch (error) {
      return false;
    }
  };

  /**
   * Valida código de autorización cambio contraseña
   * @param {string} email
   * @param {string} code - código de autenticación
   * @returns {boolean}
   */
  const validateAuthCode = async (email: string = '', code: string): Promise<boolean> => {
    try {
      const authCode = await clientRepository.validateAuthCode(email, code);
      return authCode.status === 200;
    } catch (error) {
      return false;
    }
  };

  /**
   * Reestablece la contraseña
   * @param {string} email
   * @param {string} code
   * @param {string} password
   * @returns {boolean}
   */
  const recoverPassword = async (email: string = '', code: string, password: string = ''): Promise<boolean> => {
    try {
      const authCode = await clientRepository.recoverPassword(email, code, password);
      return authCode.status === 200;
    } catch (error) {
      return false;
    }
  };

  const changePasswordClient = async (
    passwords: IChangePaswd,
    id: number = 0,
    token: string = ''
  ): Promise<IRegisterResponse> => {

    const registerResponse: IRegisterResponse = {
      data: null,
      user: null,
      errors: [],
    };

    const BACK_PASSWORD_MESSAGE_ERROR = translate('session.password_match');
    const BACK_USER_MESSAGE = translate('session.non_exist_account');

    try {
      const response = await clientRepository.changePasswordClient(passwords, id, token);  
      if (response && response.status !== 200) {
        registerResponse.errors.push({
          error: true,
          field:'EMAIL',
          message:BACK_USER_MESSAGE,
        });
      }
    } catch (error) {
      registerResponse.errors.push({
        error: true,
        field:'EMAIL',
        message:BACK_PASSWORD_MESSAGE_ERROR,
      });
    }
    

    return registerResponse;

  }

  /**
   * retorna la funciones del controlador
   */
  return {
    doLogin,
    clientRegistration,
    doLogout,
    getUser,
    updateClient,
    updatePassword,
    tokenRenovation,
    getAuthCode,
    validateAuthCode,
    recoverPassword,
    changePasswordClient,
    validateUserRegister
  };
};

export default UserController;
